import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logoIconPng from './logo-icon.svg';

/*
  LUMIX ÓTICAS — Landing Page V5
  Direção visual:
  - mesma linguagem clean do sistema interno
  - fundo branco / cinza suave
  - divisórias finas e tipografia Inter
  - sem sombras e sem gradientes
  - pouca cardização
  - desktop e mobile
  - sem dependências externas de UI
*/

const navItems = [
  { label: 'Início', href: '#inicio' },
  { label: 'Recursos', href: '#recursos' },
  { label: 'Pós-venda', href: '#pos-venda' },
  { label: 'Planos', href: '#planos' },
  { label: 'FAQ', href: '#faq' },
];

const legalLinks = [
  { label: 'Termos de Uso', href: '/termos-de-uso' },
  { label: 'Condições Comerciais', href: '/condicoes-comerciais' },
  { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
  { label: 'Política de Cookies', href: '/politica-de-cookies' },
  { label: 'Clientes Fundadores', href: '/termo-clientes-fundadores' },
];

const features = [
  {
    icon: 'receipt',
    title: 'OS organizada do início ao fim',
    text: 'Cliente, receita, fotos, anexos, pagamento, status e histórico em um único fluxo.',
  },
  {
    icon: 'users',
    title: 'CRM que acompanha o cliente',
    text: 'Atendimento, histórico e oportunidades acessíveis sem depender de planilhas ou memória da equipe.',
  },
  {
    icon: 'chart',
    title: 'Gestão financeira mais clara',
    text: 'Vendas, recebimentos, ticket médio, fluxo de caixa e visão por unidade em uma operação centralizada.',
  },
  {
    icon: 'boxes',
    title: 'Estoque por empresa',
    text: 'Produtos e armações separados por unidade, com uma visão simples para operações multiempresa.',
  },
  {
    icon: 'message',
    title: 'Pós-venda automatizado',
    text: 'Crie uma régua de relacionamento para manter sua ótica presente depois da entrega.',
  },
  {
    icon: 'spark',
    title: 'Campanhas com sua própria base',
    text: 'Aniversariantes, clientes antigos, retornos e listas importadas viram novas oportunidades.',
  },
];

const timeline = [
  { label: 'Pedido', title: 'OS criada', text: 'A venda nasce organizada.' },
  { label: 'Entrega', title: 'Marco zero', text: 'A jornada de relacionamento começa.' },
  { label: '30 dias', title: 'Avaliação', text: 'Contato leve para reforçar confiança.' },
  { label: '90 dias', title: 'Ajuste', text: 'Convite para limpeza e manutenção.' },
  { label: '360 dias', title: 'Retorno', text: 'Nova oportunidade de venda.' },
];

const plans = [
  {
    code: 'start',
    name: 'Start',
    price: 189,
    label: '1 empresa',
    description: 'Para organizar a operação completa de uma ótica.',
    features: [
      'OS completa',
      'CRM integrado',
      'Fotos e anexos',
      'Fluxo de caixa',
      'Pós-venda',
      'Controle de estoque',
      'Estoque de armações',
      'Emissão de NFC-e',
    ],
  },
  {
    code: 'crescimento',
    name: 'Crescimento',
    price: 299,
    label: 'até 2 empresas',
    featured: true,
    description: 'Para quem já opera mais de uma unidade.',
    features: [
      'Tudo do Start',
      'Multiempresa',
      'Relatórios por unidade',
      'Campanhas por loja',
      'Controle de usuários',
      'DRE operacional',
      'Gestão de clientes',
      'Visão consolidada',
    ],
  },
  {
    code: 'multiempresas',
    name: 'Multiempresas',
    price: 399,
    label: 'até 3 empresas',
    description: 'Para redes pequenas com gestão centralizada.',
    features: [
      'Tudo do Crescimento',
      'Usuários por unidade',
      'Estoque por empresa',
      'CRM por empresa',
      'Campanhas segmentadas',
      'NFC-e por empresa',
      'Dashboard gerencial',
      'Base para expansão',
    ],
  },
];

const faq = [
  {
    q: 'A Lumix funciona para ótica pequena?',
    a: 'Sim. O plano Start foi pensado para óticas que querem organizar atendimento, OS, financeiro, CRM, estoque e pós-venda sem complicar a rotina.',
  },
  {
    q: 'Dá para usar em mais de uma unidade?',
    a: 'Sim. A Lumix trabalha com operação multiempresa, mantendo os dados de cada unidade separados e permitindo uma visão consolidada da gestão.',
  },
  {
    q: 'O pós-venda é automático?',
    a: 'A régua pode ser configurada para acompanhar o cliente após a entrega e criar contatos em momentos estratégicos.',
  },
  {
    q: 'Posso trabalhar clientes antigos?',
    a: 'Sim. Você pode usar sua base de compradores antigos, aniversariantes, clientes do mês e listas importadas para novas campanhas.',
  },
  {
    q: 'O desconto do cupom vale para todos os meses?',
    a: 'Salvo condição expressa diferente, cupons são aplicados somente na primeira mensalidade.',
  },
];

function Icon({ name, size = 20 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };

  if (name === 'receipt') {
    return (
      <svg {...common}>
        <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" />
        <path d="M9 8h6M9 12h6" />
      </svg>
    );
  }

  if (name === 'users') {
    return (
      <svg {...common}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }

  if (name === 'chart') {
    return (
      <svg {...common}>
        <path d="M3 3v18h18" />
        <path d="m7 16 4-5 3 3 5-7" />
      </svg>
    );
  }

  if (name === 'boxes') {
    return (
      <svg {...common}>
        <path d="m12 2 8 4-8 4-8-4 8-4Z" />
        <path d="m4 10 8 4 8-4M4 14l8 4 8-4M4 18l8 4 8-4" />
      </svg>
    );
  }

  if (name === 'message') {
    return (
      <svg {...common}>
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
        <path d="M8 9h8M8 13h5" />
      </svg>
    );
  }

  if (name === 'spark') {
    return (
      <svg {...common}>
        <path d="m12 3-1.2 4.1A5 5 0 0 1 7.1 10L3 11.2l4.1 1.2a5 5 0 0 1 3.7 3.7L12 20l1.2-3.9a5 5 0 0 1 3.7-3.7l4.1-1.2-4.1-1.2a5 5 0 0 1-3.7-3.7L12 3Z" />
      </svg>
    );
  }

  if (name === 'moon') {
    return (
      <svg {...common}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
      </svg>
    );
  }

  if (name === 'sun') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }

  if (name === 'menu') {
    return (
      <svg {...common}>
        <path d="M4 7h16M4 12h16M4 17h16" />
      </svg>
    );
  }

  if (name === 'close') {
    return (
      <svg {...common}>
        <path d="m6 6 12 12M18 6 6 18" />
      </svg>
    );
  }

  if (name === 'arrow') {
    return (
      <svg {...common}>
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    );
  }

  if (name === 'check') {
    return (
      <svg {...common}>
        <path d="m5 12 4 4L19 6" />
      </svg>
    );
  }

  return null;
}

function BrandLogo({ size = 30, dark = false }) {
  return (
    <img
      src={logoIconPng}
      alt="Lumix Óticas"
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        display: 'block',
        objectFit: 'contain',
        filter: dark ? 'brightness(0) invert(1)' : 'none',
      }}
    />
  );
}

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '' }) {
  const [ref, visible] = useInView();

  return (
    <div
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={{ '--delay': `${delay}s` }}
    >
      {children}
    </div>
  );
}

export default function Landing() {
  const [dark, setDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const year = useMemo(() => new Date().getFullYear(), []);
  const t = buildTokens(dark);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="landing-root" style={{ background: t.bg, color: t.text }}>
      <style>{buildCss(t, dark)}</style>

      <header className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="wrap nav-inner">
          <a href="#inicio" className="brand" onClick={closeMenu}>
            <BrandLogo size={30} dark={dark} />
            <span className="brand-name">
              Lumix&nbsp;
              <span className="brand-letter brand-blue">Ó</span>
              <span className="brand-letter brand-red">t</span>
              <span className="brand-letter brand-yellow">i</span>
              <span className="brand-letter brand-green">c</span>
              <span className="brand-letter brand-blue">a</span>
              <span className="brand-letter brand-red">s</span>
            </span>
          </a>

          <nav className="desktop-nav" aria-label="Navegação principal">
            {navItems.map((item) => (
              <a key={item.href} href={item.href} className="nav-link">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="nav-actions">
            <button
              type="button"
              className="icon-button"
              aria-label="Alternar tema"
              title="Alternar tema"
              onClick={() => setDark((value) => !value)}
            >
              <Icon name={dark ? 'sun' : 'moon'} size={19} />
            </button>

            <Link to="/login" className="btn btn-secondary nav-login">
              Entrar
            </Link>

            <Link to="/checkout/start" className="btn btn-primary nav-cta">
              Solicitar acesso
            </Link>

            <button
              type="button"
              className="menu-button"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="mobile-menu-shell">
            <div className="mobile-menu">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} className="mobile-link" onClick={closeMenu}>
                  {item.label}
                </a>
              ))}

              <div className="mobile-menu-actions">
                <Link to="/login" className="btn btn-secondary" onClick={closeMenu}>
                  Entrar
                </Link>
                <Link to="/checkout/start" className="btn btn-primary" onClick={closeMenu}>
                  Solicitar acesso
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <section id="inicio" className="hero">
          <div className="hero-glow hero-glow-a" />
          <div className="hero-glow hero-glow-b" />

          <div className="wrap hero-grid">
            <div className="hero-copy">
              <Reveal>
                <div className="eyebrow">
                  <span className="eyebrow-dot" />
                  Gestão completa para óticas
                </div>

                <h1>
                  Sua ótica organizada.
                  <span> Seu crescimento mais simples.</span>
                </h1>

                <p className="hero-description">
                  OS, clientes, financeiro, estoque, CRM, campanhas e pós-venda em uma plataforma criada para a rotina de óticas.
                </p>

                <div className="hero-actions">
                  <Link to="/checkout/start" className="btn btn-primary btn-large">
                    Solicitar acesso
                    <Icon name="arrow" size={18} />
                  </Link>

                  <a href="#recursos" className="btn btn-secondary btn-large">
                    Conhecer a plataforma
                  </a>
                </div>

                <div className="hero-proof">
                  <div>
                    <strong>30 · 60 · 90</strong>
                    <span>régua de relacionamento</span>
                  </div>
                  <div>
                    <strong>360 dias</strong>
                    <span>campanha de retorno</span>
                  </div>
                  <div>
                    <strong>Multiempresa</strong>
                    <span>dados separados por unidade</span>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.08} className="hero-product-wrap">
              <div className="product-window">
                <div className="product-topbar">
                  <div className="product-brand-mini">
                    <span className="status-dot" />
                    Ótica Centro
                  </div>
                  <span>Hoje</span>
                </div>

                <div className="product-content">
                  <div className="dashboard-heading">
                    <div>
                      <span className="dashboard-label">Vendas do mês</span>
                      <strong>R$ 48.920</strong>
                    </div>
                    <span className="positive-badge">+18%</span>
                  </div>

                  <div className="mini-chart" aria-hidden="true">
                    <svg viewBox="0 0 520 150" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#1a73e8" stopOpacity="0.18" />
                          <stop offset="100%" stopColor="#1a73e8" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        className="chart-area"
                        d="M0 122 C44 116 63 108 94 110 C134 112 145 81 188 87 C235 95 252 60 292 65 C336 71 350 44 388 49 C430 54 460 31 520 23 L520 150 L0 150 Z"
                        fill="url(#chartFill)"
                      />
                      <path
                        className="chart-line"
                        d="M0 122 C44 116 63 108 94 110 C134 112 145 81 188 87 C235 95 252 60 292 65 C336 71 350 44 388 49 C430 54 460 31 520 23"
                        fill="none"
                        stroke="#36564c"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <div className="metric-grid">
                    <div className="metric-card">
                      <span>Pedido</span>
                      <strong>12</strong>
                    </div>
                    <div className="metric-card">
                      <span>Laboratório</span>
                      <strong>9</strong>
                    </div>
                    <div className="metric-card">
                      <span>Pronto</span>
                      <strong>8</strong>
                    </div>
                  </div>

                  <div className="notification-card">
                    <div className="notification-icon">
                      <Icon name="message" size={19} />
                    </div>
                    <div className="notification-copy">
                      <strong>OS #1048 pronta</strong>
                      <span>Mensagem pronta para o cliente.</span>
                    </div>
                    <button type="button">Enviar</button>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="trust-bar">
          <div className="wrap trust-grid">
            <span>Atendimento</span>
            <span>Ordens de serviço</span>
            <span>Financeiro</span>
            <span>Estoque</span>
            <span>CRM</span>
            <span>Pós-venda</span>
          </div>
        </section>

        <section id="recursos" className="section">
          <div className="wrap">
            <Reveal>
              <div className="section-heading">
                <div className="section-kicker">Tudo conectado</div>
                <h2>Menos sistema espalhado.<br />Mais operação em um só lugar.</h2>
                <p>
                  A Lumix concentra as partes que realmente fazem diferença na rotina da ótica, sem transformar a tela em um painel cheio de ruído.
                </p>
              </div>
            </Reveal>

            <div className="feature-grid">
              {features.map((feature, index) => (
                <Reveal key={feature.title} delay={index * 0.04}>
                  <article className="feature-card">
                    <div className="feature-icon">
                      <Icon name={feature.icon} size={22} />
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="pos-venda" className="section section-soft">
          <div className="wrap relationship-grid">
            <Reveal className="relationship-copy">
              <div className="section-kicker">Relacionamento automático</div>
              <h2>A entrega não precisa ser o fim da venda.</h2>
              <p>
                A Lumix transforma a data de entrega em uma jornada de relacionamento para sua ótica continuar presente no momento certo.
              </p>

              <div className="timeline">
                {timeline.map((item, index) => (
                  <div className="timeline-row" key={item.label}>
                    <div className="timeline-index">{String(index + 1).padStart(2, '0')}</div>
                    <div>
                      <span>{item.label}</span>
                      <strong>{item.title}</strong>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.08} className="phone-column">
              <div className="phone-card">
                <div className="phone-status">
                  <span>9:41</span>
                  <div className="phone-status-icons">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>

                <div className="chat-header">
                  <div className="chat-avatar">L</div>
                  <div>
                    <strong>Lumix Óticas</strong>
                    <span>pós-venda ativo</span>
                  </div>
                </div>

                <div className="chat-body">
                  <div className="chat-date">Hoje</div>

                  <div className="bubble bubble-in">
                    Olá, Ana! Seus óculos estão com 90 dias de uso. Que tal passar para uma limpeza e ajuste? 👓
                  </div>

                  <div className="bubble bubble-out">
                    Nossa, eu estava precisando mesmo!
                  </div>

                  <div className="bubble bubble-in">
                    Perfeito. Quando você chegar, sua equipe já terá o histórico do atendimento.
                  </div>

                  <div className="chat-read">✓✓ Entregue agora</div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="section founder-section">
          <div className="wrap">
            <Reveal>
              <div className="founder-banner">
                <div>
                  <div className="section-kicker light">Implantação assistida</div>
                  <h2>Comece com acompanhamento próximo.</h2>
                  <p>
                    Configuração assistida, suporte direto e evolução com base no uso real da operação.
                  </p>
                </div>

                <div className="founder-side">
                  <div className="founder-number">
                    <strong>10–20</strong>
                    <span>óticas no grupo inicial</span>
                  </div>
                  <Link to="/checkout/start" className="btn btn-white btn-large">
                    Solicitar acesso
                    <Icon name="arrow" size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section id="planos" className="section section-soft">
          <div className="wrap">
            <Reveal>
              <div className="section-heading centered">
                <div className="section-kicker">Planos</div>
                <h2>Escolha o tamanho da sua operação.</h2>
                <p>Comece com uma empresa e evolua conforme sua estrutura cresce.</p>
              </div>
            </Reveal>

            <div className="plans-grid">
              {plans.map((plan, index) => (
                <Reveal key={plan.code} delay={index * 0.05}>
                  <article className={`plan-card ${plan.featured ? 'plan-featured' : ''}`}>
                    {plan.featured && <div className="popular-badge">Mais popular</div>}

                    <div className="plan-top">
                      <span className="plan-label">{plan.label}</span>
                      <h3>{plan.name}</h3>
                      <p>{plan.description}</p>
                    </div>

                    <div className="plan-price">
                      <span>R$</span>
                      <strong>{plan.price}</strong>
                      <small>/mês</small>
                    </div>

                    <div className="plan-divider" />

                    <ul>
                      {plan.features.map((feature) => (
                        <li key={feature}>
                          <span className="check-icon"><Icon name="check" size={14} /></span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={`/checkout/${plan.code}`}
                      className={`btn btn-large btn-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      Escolher plano
                    </Link>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id="faq" className="section">
          <div className="wrap faq-grid">
            <Reveal>
              <div className="faq-copy">
                <div className="section-kicker">FAQ</div>
                <h2>O essencial antes de começar.</h2>
                <p>Dúvidas rápidas sobre operação, planos e funcionamento da Lumix.</p>

                <Link to="/checkout/start" className="text-link">
                  Solicitar acesso <Icon name="arrow" size={17} />
                </Link>
              </div>
            </Reveal>

            <div className="faq-list">
              {faq.map((item, index) => {
                const active = openFaq === index;

                return (
                  <Reveal key={item.q} delay={index * 0.035}>
                    <article className={`faq-item ${active ? 'faq-open' : ''}`}>
                      <button
                        type="button"
                        className="faq-question"
                        aria-expanded={active}
                        onClick={() => setOpenFaq(active ? null : index)}
                      >
                        <span>{item.q}</span>
                        <span className="faq-toggle">{active ? '−' : '+'}</span>
                      </button>

                      <div className="faq-answer-wrap">
                        <div className="faq-answer">
                          <p>{item.a}</p>
                        </div>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="final-cta-section">
          <div className="wrap">
            <Reveal>
              <div className="final-cta">
                <div>
                  <span>Pronto para organizar sua operação?</span>
                  <h2>Leve sua ótica para uma gestão mais simples.</h2>
                </div>

                <Link to="/checkout/start" className="btn btn-primary btn-large">
                  Solicitar acesso
                  <Icon name="arrow" size={18} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="wrap footer-main">
          <div className="footer-brand">
            <a href="#inicio" className="brand">
              <BrandLogo size={28} dark={dark} />
              <span className="brand-name">
              Lumix&nbsp;
              <span className="brand-letter brand-blue">Ó</span>
              <span className="brand-letter brand-red">t</span>
              <span className="brand-letter brand-yellow">i</span>
              <span className="brand-letter brand-green">c</span>
              <span className="brand-letter brand-blue">a</span>
              <span className="brand-letter brand-red">s</span>
            </span>
            </a>
            <p>Gestão, OS, vendas, estoque, financeiro, campanhas e pós-venda para óticas.</p>
          </div>

          <div className="footer-links">
            {legalLinks.map((item) => (
              <Link key={item.href} to={item.href}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="wrap footer-bottom">
          <span>© {year} Lumix Tecnologia. Todos os direitos reservados.</span>
          <span>Plataforma em implantação comercial.</span>
        </div>
      </footer>

      <div className="mobile-sticky">
        <div>
          <strong>Lumix Óticas</strong>
          <span>Start · R$ 189/mês</span>
        </div>
        <Link to="/checkout/start" className="btn btn-primary">
          Acessar
        </Link>
      </div>
    </div>
  );
}

function buildTokens(dark) {
  return dark
    ? {
        bg: '#151817',
        bgSoft: '#1c201f',
        surface: '#151817',
        surface2: '#1c201f',
        text: '#f1f4f3',
        text2: '#d7dddb',
        muted: '#9aa4a1',
        soft: '#78817f',
        border: '#2b302f',
        borderStrong: '#3a413f',
        accent: '#a9c4bb',
        accentStrong: '#c1d6cf',
        accentSoft: '#202a27',
        success: '#78b89f',
        danger: '#e18b84',
        nav: '#151817',
      }
    : {
        bg: '#ffffff',
        bgSoft: '#f7f9f9',
        surface: '#ffffff',
        surface2: '#f7f9f9',
        text: '#172020',
        text2: '#374642',
        muted: '#74807d',
        soft: '#9aa3a1',
        border: '#e8ecec',
        borderStrong: '#d8dfde',
        accent: '#36564c',
        accentStrong: '#2e4a42',
        accentSoft: '#eef4f2',
        success: '#2f6b57',
        danger: '#a63e36',
        nav: '#ffffff',
      };
}

function buildCss(t, dark) {
  return `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

:root {
  --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: 68px;
}

body {
  margin: 0;
  background: ${t.bg};
}

button,
input,
a {
  font: inherit;
}

button,
a {
  -webkit-tap-highlight-color: transparent;
}

button:focus-visible,
a:focus-visible {
  outline: 2px solid ${t.accent};
  outline-offset: 3px;
}

.landing-root {
  min-height: 100vh;
  overflow-x: hidden;
  color: ${t.text};
  background: ${t.bg};
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.wrap {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.reveal {
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity .28s ease var(--delay),
    transform .28s ease var(--delay);
}

.reveal.is-visible {
  opacity: 1;
  transform: none;
}

/* =========================================================
   NAV — MESMA LINGUAGEM DA APLICAÇÃO
========================================================= */
.nav {
  position: fixed;
  inset: 0 0 auto;
  z-index: 100;
  background: ${t.nav};
  border-bottom: 1px solid ${t.border};
  box-shadow: none;
  transition: background .14s ease, border-color .14s ease;
}

.nav-scrolled {
  background: ${t.nav};
  border-color: ${t.borderStrong};
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.nav-inner {
  min-height: 60px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  color: ${t.text};
  text-decoration: none;
}

.brand-name {
  font-size: 15px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -.035em;
  white-space: nowrap;
}

.brand-letter {
  display: inline;
}

.brand-blue { color: #4285F4; }
.brand-red { color: #EA4335; }
.brand-yellow { color: #FBBC04; }
.brand-green { color: #34A853; }

.desktop-nav {
  margin-left: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.nav-link {
  padding: 7px 9px;
  color: ${t.muted};
  text-decoration: none;
  font-size: 10px;
  line-height: 1;
  font-weight: 650;
  transition: color .12s ease, background .12s ease;
}

.nav-link:hover {
  color: ${t.text};
  background: ${t.bgSoft};
}

.nav-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-button,
.menu-button {
  width: 34px;
  height: 34px;
  padding: 0;
  display: grid;
  place-items: center;
  border: 1px solid ${t.border};
  border-radius: 6px;
  color: ${t.muted};
  background: ${t.surface};
  cursor: pointer;
  box-shadow: none;
  transition: color .12s ease, border-color .12s ease, background .12s ease;
}

.icon-button:hover,
.menu-button:hover {
  color: ${t.text};
  border-color: ${t.borderStrong};
  background: ${t.bgSoft};
}

.menu-button {
  display: none;
}

.mobile-menu-shell {
  display: none;
}

/* =========================================================
   BOTÕES — FLAT
========================================================= */
.btn {
  min-height: 36px;
  padding: 0 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid ${t.borderStrong};
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  box-shadow: none;
  transition:
    color .12s ease,
    background .12s ease,
    border-color .12s ease;
}

.btn:hover {
  transform: none;
  box-shadow: none;
}

.btn-primary {
  color: ${dark ? '#151817' : '#ffffff'};
  background: ${t.accent};
  border-color: ${t.accent};
  box-shadow: none;
}

.btn-primary:hover {
  color: ${dark ? '#151817' : '#ffffff'};
  background: ${t.accentStrong};
  border-color: ${t.accentStrong};
  box-shadow: none;
}

.btn-secondary {
  color: ${t.text};
  background: ${t.surface};
  border-color: ${t.borderStrong};
}

.btn-secondary:hover {
  background: ${t.bgSoft};
  border-color: ${t.text2};
}

.btn-white {
  color: ${t.text};
  background: ${t.surface};
  border-color: ${t.borderStrong};
  box-shadow: none;
}

.btn-large {
  min-height: 42px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 11px;
}

.btn-full {
  width: 100%;
}

/* =========================================================
   HERO — SEM GLOW, SEM CARTÃO FLUTUANTE
========================================================= */
.hero {
  position: relative;
  padding: 116px 0 64px;
  background: ${t.bg};
  overflow: hidden;
}

.hero-glow,
.hero-glow-a,
.hero-glow-b {
  display: none;
}

.hero-grid {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, .95fr) minmax(410px, 1.05fr);
  gap: 64px;
  align-items: center;
}

.eyebrow,
.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: ${t.muted};
  font-size: 8px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: .065em;
  text-transform: uppercase;
}

.eyebrow-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${t.accent};
  box-shadow: none;
}

.hero h1 {
  max-width: 650px;
  margin: 14px 0 15px;
  color: ${t.text};
  font-size: clamp(38px, 4.6vw, 58px);
  line-height: 1.01;
  letter-spacing: -.055em;
  font-weight: 750;
}

.hero h1 span {
  color: ${t.text};
}

.hero-description {
  max-width: 570px;
  margin: 0;
  color: ${t.muted};
  font-size: 13px;
  line-height: 1.7;
  font-weight: 500;
}

.hero-actions {
  margin-top: 23px;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.hero-proof {
  margin-top: 29px;
  padding-top: 18px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 18px;
  border-top: 1px solid ${t.border};
}

.hero-proof strong,
.hero-proof span {
  display: block;
}

.hero-proof strong {
  margin-bottom: 3px;
  color: ${t.text};
  font-size: 10px;
  line-height: 1.25;
  font-weight: 750;
}

.hero-proof span {
  color: ${t.muted};
  font-size: 8.5px;
  line-height: 1.45;
  font-weight: 500;
}

.hero-product-wrap {
  width: 100%;
}

.product-window {
  position: relative;
  overflow: hidden;
  border: 1px solid ${t.borderStrong};
  border-radius: 6px;
  background: ${t.surface};
  box-shadow: none;
}

.product-window::after {
  display: none;
}

.product-topbar {
  min-height: 45px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid ${t.border};
  color: ${t.muted};
  font-size: 8.5px;
  font-weight: 600;
}

.product-brand-mini {
  display: flex;
  align-items: center;
  gap: 7px;
  color: ${t.text};
  font-weight: 700;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${t.success};
  box-shadow: none;
}

.product-content {
  padding: 18px;
}

.dashboard-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.dashboard-label {
  display: block;
  margin-bottom: 5px;
  color: ${t.muted};
  font-size: 8.5px;
  font-weight: 650;
}

.dashboard-heading strong {
  display: block;
  color: ${t.text};
  font-size: clamp(27px, 3.2vw, 36px);
  line-height: 1;
  letter-spacing: -.04em;
  font-weight: 750;
}

.positive-badge {
  padding: 4px 6px;
  border: 1px solid ${t.border};
  border-radius: 4px;
  color: ${t.success};
  background: transparent;
  font-size: 8px;
  font-weight: 750;
}

.mini-chart {
  height: 118px;
  margin: 8px 0 2px;
  overflow: hidden;
  border-bottom: 1px solid ${t.border};
}

.mini-chart svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.chart-area {
  display: none;
}

.chart-line {
  stroke-width: 2 !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 0;
  border-bottom: 1px solid ${t.border};
}

.metric-card {
  padding: 12px 10px;
  border: 0;
  border-right: 1px solid ${t.border};
  border-radius: 0;
  background: transparent;
}

.metric-card:last-child {
  border-right: 0;
}

.metric-card span,
.metric-card strong {
  display: block;
}

.metric-card span {
  margin-bottom: 5px;
  color: ${t.muted};
  font-size: 7px;
  letter-spacing: .045em;
  text-transform: uppercase;
  font-weight: 650;
}

.metric-card strong {
  color: ${t.text};
  font-size: 18px;
  line-height: 1;
  font-weight: 750;
}

.notification-card {
  margin-top: 0;
  padding: 11px 0 0;
  display: grid;
  grid-template-columns: 29px 1fr auto;
  gap: 9px;
  align-items: center;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.notification-icon {
  width: 29px;
  height: 29px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 0;
  color: ${t.accent};
  background: transparent;
}

.notification-copy {
  min-width: 0;
}

.notification-copy strong,
.notification-copy span {
  display: block;
}

.notification-copy strong {
  margin-bottom: 2px;
  color: ${t.text};
  font-size: 9px;
  font-weight: 700;
}

.notification-copy span {
  overflow: hidden;
  color: ${t.muted};
  font-size: 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.notification-card button {
  min-height: 28px;
  padding: 0 9px;
  border: 1px solid ${t.borderStrong};
  border-radius: 5px;
  color: ${t.text};
  background: ${t.surface};
  cursor: pointer;
  font-size: 8px;
  font-weight: 700;
}

/* =========================================================
   TRUST
========================================================= */
.trust-bar {
  border-top: 1px solid ${t.border};
  border-bottom: 1px solid ${t.border};
  background: ${t.bgSoft};
}

.trust-grid {
  min-height: 54px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0,1fr));
  align-items: center;
}

.trust-grid span {
  position: relative;
  padding: 0 8px;
  color: ${t.muted};
  text-align: center;
  font-size: 8.5px;
  font-weight: 650;
}

.trust-grid span + span::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 1px;
  height: 15px;
  background: ${t.border};
  transform: translateY(-50%);
}

/* =========================================================
   SEÇÕES
========================================================= */
.section {
  padding: 76px 0;
  background: ${t.bg};
}

.section-soft {
  background: ${t.bgSoft};
  border-top: 1px solid ${t.border};
  border-bottom: 1px solid ${t.border};
}

.section-heading {
  max-width: 690px;
  margin-bottom: 30px;
}

.section-heading.centered {
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.section-heading h2,
.relationship-copy h2,
.faq-copy h2,
.founder-banner h2,
.final-cta h2 {
  margin: 10px 0 11px;
  color: ${t.text};
  font-size: clamp(27px, 3.4vw, 40px);
  line-height: 1.08;
  letter-spacing: -.04em;
  font-weight: 750;
}

.section-heading p,
.relationship-copy > p,
.faq-copy > p {
  max-width: 620px;
  margin: 0;
  color: ${t.muted};
  font-size: 11px;
  line-height: 1.7;
  font-weight: 500;
}

.centered p {
  margin-left: auto;
  margin-right: auto;
}

/* =========================================================
   RECURSOS — LINHAS, NÃO CARDS
========================================================= */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 0;
  border-top: 1px solid ${t.border};
  border-bottom: 1px solid ${t.border};
}

.feature-grid > .reveal {
  min-width: 0;
  border-right: 1px solid ${t.border};
  border-bottom: 1px solid ${t.border};
}

.feature-grid > .reveal:nth-child(3n) {
  border-right: 0;
}

.feature-grid > .reveal:nth-last-child(-n + 3) {
  border-bottom: 0;
}

.feature-card {
  height: 100%;
  min-height: 150px;
  padding: 20px 18px;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  transition: background .12s ease;
}

.feature-card:hover {
  transform: none;
  border-color: transparent;
  background: ${t.bgSoft};
  box-shadow: none;
}

.feature-icon {
  width: 28px;
  height: 28px;
  margin-bottom: 15px;
  display: grid;
  place-items: center;
  border-radius: 0;
  color: ${t.accent};
  background: transparent;
}

.feature-card h3 {
  margin: 0 0 6px;
  color: ${t.text};
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: -.015em;
  font-weight: 750;
}

.feature-card p {
  margin: 0;
  color: ${t.muted};
  font-size: 9px;
  line-height: 1.65;
  font-weight: 500;
}

/* =========================================================
   PÓS-VENDA
========================================================= */
.relationship-grid {
  display: grid;
  grid-template-columns: minmax(0,1fr) 390px;
  gap: 72px;
  align-items: center;
}

.relationship-copy {
  max-width: 650px;
}

.timeline {
  margin-top: 28px;
  display: grid;
  border-top: 1px solid ${t.border};
}

.timeline-row {
  position: relative;
  min-height: 65px;
  padding: 13px 0 13px 43px;
  display: grid;
  border-bottom: 1px solid ${t.border};
}

.timeline-row:not(:last-child)::after {
  display: none;
}

.timeline-index {
  position: absolute;
  left: 0;
  top: 13px;
  width: 27px;
  height: 27px;
  display: grid;
  place-items: center;
  border: 1px solid ${t.borderStrong};
  border-radius: 4px;
  color: ${t.accent};
  background: transparent;
  font-size: 7px;
  font-weight: 750;
}

.timeline-row span,
.timeline-row strong {
  display: block;
}

.timeline-row span {
  margin-bottom: 2px;
  color: ${t.muted};
  font-size: 7px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
}

.timeline-row strong {
  color: ${t.text};
  font-size: 10px;
  font-weight: 700;
}

.timeline-row p {
  margin: 3px 0 0;
  color: ${t.muted};
  font-size: 8.5px;
  line-height: 1.45;
}

.phone-column {
  display: flex;
  justify-content: center;
}

.phone-card {
  width: min(330px,100%);
  padding: 7px;
  border: 1px solid ${t.borderStrong};
  border-radius: 22px;
  background: ${dark ? '#101312' : '#f1f1f1'};
  box-shadow: none;
}

.phone-status {
  height: 28px;
  padding: 0 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${dark ? '#f1f4f3' : '#172020'};
  font-size: 7px;
  font-weight: 700;
}

.phone-status-icons {
  display: flex;
  align-items: center;
  gap: 3px;
}

.phone-status-icons span {
  width: 8px;
  height: 4px;
  border: 1px solid currentColor;
  border-radius: 2px;
}

.chat-header {
  padding: 11px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 15px 15px 0 0;
  background: ${t.surface};
  border-bottom: 1px solid ${t.border};
}

.chat-avatar {
  width: 31px;
  height: 31px;
  display: grid;
  place-items: center;
  border: 1px solid ${t.borderStrong};
  border-radius: 50%;
  color: ${t.accent};
  background: ${t.surface};
  font-size: 9px;
  font-weight: 750;
}

.chat-header strong,
.chat-header span {
  display: block;
}

.chat-header strong {
  color: ${t.text};
  font-size: 9px;
  font-weight: 700;
}

.chat-header span {
  margin-top: 2px;
  color: ${t.success};
  font-size: 7px;
  font-weight: 650;
}

.chat-body {
  min-height: 350px;
  padding: 15px 11px 12px;
  border-radius: 0 0 16px 16px;
  background: ${t.bgSoft};
}

.chat-date {
  width: max-content;
  margin: 0 auto 12px;
  padding: 3px 6px;
  border: 1px solid ${t.border};
  border-radius: 4px;
  color: ${t.muted};
  background: ${t.surface};
  font-size: 6.5px;
  font-weight: 650;
}

.bubble {
  width: max-content;
  max-width: 82%;
  margin-bottom: 8px;
  padding: 8px 9px;
  border: 1px solid ${t.border};
  border-radius: 6px;
  color: ${t.text2};
  background: ${t.surface};
  font-size: 8px;
  line-height: 1.5;
}

.bubble-out {
  margin-left: auto;
  border-color: ${t.borderStrong};
  background: ${t.accentSoft};
}

.chat-read {
  margin-top: 5px;
  color: ${t.muted};
  text-align: right;
  font-size: 6.5px;
  font-weight: 600;
}

/* =========================================================
   IMPLANTAÇÃO — NÃO É BANNER FLUTUANTE
========================================================= */
.founder-section {
  padding: 0;
  background: ${t.bg};
}

.founder-banner {
  padding: 34px 0;
  display: grid;
  grid-template-columns: minmax(0,1fr) auto;
  align-items: center;
  gap: 40px;
  border-top: 1px solid ${t.border};
  border-bottom: 1px solid ${t.border};
  border-radius: 0;
  color: ${t.text};
  background: transparent;
  box-shadow: none;
}

.section-kicker.light {
  color: ${t.muted};
}

.founder-banner p {
  max-width: 610px;
  margin: 0;
  color: ${t.muted};
  font-size: 10px;
  line-height: 1.65;
}

.founder-side {
  display: flex;
  align-items: center;
  gap: 24px;
}

.founder-number {
  min-width: 105px;
}

.founder-number strong,
.founder-number span {
  display: block;
}

.founder-number strong {
  color: ${t.text};
  font-size: 20px;
  line-height: 1;
  font-weight: 750;
}

.founder-number span {
  margin-top: 5px;
  color: ${t.muted};
  font-size: 7.5px;
  line-height: 1.4;
  font-weight: 550;
}

/* =========================================================
   PLANOS — COLUNAS FLAT
========================================================= */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 0;
  border-top: 1px solid ${t.borderStrong};
  border-bottom: 1px solid ${t.borderStrong};
}

.plans-grid > .reveal {
  min-width: 0;
  border-right: 1px solid ${t.border};
}

.plans-grid > .reveal:last-child {
  border-right: 0;
}

.plan-card {
  position: relative;
  height: 100%;
  min-height: 520px;
  padding: 24px 22px;
  display: flex;
  flex-direction: column;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  transition: background .12s ease;
}

.plan-card:hover {
  transform: none;
  background: ${t.surface};
  box-shadow: none;
}

.plan-featured {
  border: 0;
  background: ${t.surface};
  box-shadow: inset 0 2px 0 ${t.accent};
}

.popular-badge {
  position: static;
  width: max-content;
  margin: -2px 0 12px;
  padding: 3px 5px;
  border: 1px solid ${t.borderStrong};
  border-radius: 4px;
  color: ${t.accent};
  background: transparent;
  font-size: 7px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.plan-label {
  display: block;
  margin-bottom: 7px;
  color: ${t.muted};
  font-size: 7.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
}

.plan-top h3 {
  margin: 0 0 6px;
  color: ${t.text};
  font-size: 17px;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: -.025em;
}

.plan-top p {
  min-height: 38px;
  margin: 0;
  color: ${t.muted};
  font-size: 9px;
  line-height: 1.55;
}

.plan-price {
  margin-top: 20px;
  display: flex;
  align-items: flex-end;
  gap: 3px;
  color: ${t.text};
}

.plan-price > span {
  padding-bottom: 4px;
  font-size: 8px;
  font-weight: 700;
}

.plan-price strong {
  font-size: 32px;
  line-height: .95;
  font-weight: 750;
  letter-spacing: -.045em;
}

.plan-price small {
  padding-bottom: 3px;
  color: ${t.muted};
  font-size: 8px;
  font-weight: 500;
}

.plan-divider {
  height: 1px;
  margin: 18px 0;
  background: ${t.border};
}

.plan-card ul {
  margin: 0 0 22px;
  padding: 0;
  display: grid;
  gap: 8px;
  list-style: none;
  flex: 1;
}

.plan-card li {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  color: ${t.text2};
  font-size: 8.5px;
  line-height: 1.45;
  font-weight: 500;
}

.check-icon {
  width: 15px;
  height: 15px;
  margin-top: 0;
  display: grid;
  place-items: center;
  color: ${t.accent};
  flex: 0 0 auto;
}

/* =========================================================
   FAQ
========================================================= */
.faq-grid {
  display: grid;
  grid-template-columns: 310px minmax(0,1fr);
  gap: 72px;
  align-items: start;
}

.faq-copy {
  position: sticky;
  top: 90px;
}

.text-link {
  margin-top: 18px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${t.accent};
  text-decoration: none;
  font-size: 9px;
  font-weight: 700;
}

.faq-list {
  border-top: 1px solid ${t.borderStrong};
}

.faq-item {
  border: 0;
  border-bottom: 1px solid ${t.border};
  border-radius: 0;
  background: transparent;
  overflow: hidden;
  box-shadow: none;
}

.faq-question {
  width: 100%;
  min-height: 58px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 0;
  color: ${t.text};
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 10px;
  line-height: 1.45;
  font-weight: 700;
}

.faq-question:hover {
  color: ${t.accent};
}

.faq-toggle {
  color: ${t.muted};
  font-size: 16px;
  line-height: 1;
  font-weight: 400;
}

.faq-answer-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows .18s ease;
}

.faq-open .faq-answer-wrap {
  grid-template-rows: 1fr;
}

.faq-answer {
  overflow: hidden;
}

.faq-answer p {
  margin: 0;
  padding: 0 36px 17px 0;
  color: ${t.muted};
  font-size: 9px;
  line-height: 1.65;
}

/* =========================================================
   CTA FINAL
========================================================= */
.final-cta-section {
  padding: 0;
  background: ${t.bg};
}

.final-cta {
  padding: 38px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  border-top: 1px solid ${t.borderStrong};
  background: transparent;
}

.final-cta > div > span {
  color: ${t.muted};
  font-size: 8px;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
}

/* =========================================================
   FOOTER
========================================================= */
.footer {
  padding: 38px 0 18px;
  border-top: 1px solid ${t.border};
  background: ${t.bgSoft};
}

.footer-main {
  display: grid;
  grid-template-columns: minmax(0,1fr) minmax(300px, auto);
  gap: 50px;
  align-items: start;
}

.footer-brand p {
  max-width: 470px;
  margin: 13px 0 0;
  color: ${t.muted};
  font-size: 8.5px;
  line-height: 1.6;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0,1fr));
  gap: 8px 22px;
}

.footer-links a {
  color: ${t.muted};
  text-decoration: none;
  font-size: 8px;
  line-height: 1.4;
  font-weight: 600;
}

.footer-links a:hover {
  color: ${t.text};
}

.footer-bottom {
  margin-top: 27px;
  padding-top: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-top: 1px solid ${t.border};
  color: ${t.soft};
  font-size: 7.5px;
  line-height: 1.4;
}

/* =========================================================
   MOBILE STICKY
========================================================= */
.mobile-sticky {
  display: none;
}

/* =========================================================
   RESPONSIVE
========================================================= */
@media (max-width: 980px) {
  .desktop-nav {
    display: none;
  }

  .nav-cta {
    display: none;
  }

  .menu-button {
    display: grid;
  }

  .mobile-menu-shell {
    display: block;
    border-top: 1px solid ${t.border};
    background: ${t.bg};
  }

  .mobile-menu {
    width: min(100%, 1180px);
    margin: 0 auto;
    padding: 10px 20px 16px;
    display: grid;
  }

  .mobile-link {
    padding: 11px 0;
    border-bottom: 1px solid ${t.border};
    color: ${t.text};
    text-decoration: none;
    font-size: 10px;
    font-weight: 650;
  }

  .mobile-menu-actions {
    padding-top: 13px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 7px;
  }

  .hero-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .hero-copy {
    max-width: 730px;
  }

  .hero-product-wrap {
    max-width: 700px;
  }

  .relationship-grid {
    grid-template-columns: minmax(0,1fr) 340px;
    gap: 42px;
  }

  .faq-grid {
    grid-template-columns: 250px minmax(0,1fr);
    gap: 45px;
  }
}

@media (max-width: 760px) {
  html {
    scroll-padding-top: 58px;
  }

  .wrap {
    width: calc(100% - 28px);
  }

  .nav-inner {
    min-height: 56px;
    gap: 12px;
  }

  .brand-name {
    font-size: 14px;
  }

  .brand img {
    width: 26px !important;
    height: 26px !important;
  }

  .nav-login,
  .icon-button {
    display: none;
  }

  .menu-button {
    width: 32px;
    height: 32px;
  }

  .hero {
    padding: 92px 0 48px;
  }

  .hero-grid {
    gap: 31px;
  }

  .hero h1 {
    margin-top: 11px;
    font-size: clamp(34px, 11.5vw, 48px);
  }

  .hero-description {
    font-size: 11px;
  }

  .hero-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .btn-large {
    width: 100%;
  }

  .hero-proof {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .hero-proof div {
    padding: 9px 0;
    border-bottom: 1px solid ${t.border};
  }

  .hero-proof div:last-child {
    border-bottom: 0;
  }

  .product-window {
    border-radius: 4px;
  }

  .product-content {
    padding: 14px;
  }

  .mini-chart {
    height: 90px;
  }

  .dashboard-heading strong {
    font-size: 27px;
  }

  .metric-card {
    padding: 10px 7px;
  }

  .metric-card strong {
    font-size: 16px;
  }

  .trust-grid {
    min-height: auto;
    grid-template-columns: repeat(2, minmax(0,1fr));
  }

  .trust-grid span {
    min-height: 38px;
    display: grid;
    place-items: center;
    border-bottom: 1px solid ${t.border};
  }

  .trust-grid span:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  .trust-grid span + span::before {
    display: none;
  }

  .trust-grid span:nth-child(odd) {
    border-right: 1px solid ${t.border};
  }

  .section {
    padding: 54px 0;
  }

  .section-heading {
    margin-bottom: 22px;
  }

  .section-heading.centered {
    text-align: left;
  }

  .centered p {
    margin-left: 0;
    margin-right: 0;
  }

  .section-heading h2,
  .relationship-copy h2,
  .faq-copy h2,
  .founder-banner h2,
  .final-cta h2 {
    font-size: 27px;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }

  .feature-grid > .reveal {
    border-right: 0;
    border-bottom: 1px solid ${t.border};
  }

  .feature-grid > .reveal:nth-last-child(-n + 3) {
    border-bottom: 1px solid ${t.border};
  }

  .feature-grid > .reveal:last-child {
    border-bottom: 0;
  }

  .feature-card {
    min-height: 0;
    padding: 16px 0;
    display: grid;
    grid-template-columns: 28px minmax(0,1fr);
    column-gap: 10px;
  }

  .feature-icon {
    margin: 0;
    grid-row: 1 / span 2;
  }

  .feature-card h3 {
    align-self: end;
  }

  .feature-card p {
    grid-column: 2;
  }

  .relationship-grid {
    grid-template-columns: 1fr;
    gap: 35px;
  }

  .phone-column {
    justify-content: flex-start;
  }

  .phone-card {
    width: 100%;
    max-width: 330px;
  }

  .founder-banner {
    grid-template-columns: 1fr;
    gap: 24px;
    padding: 30px 0;
  }

  .founder-side {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }

  .founder-side .btn {
    width: 100%;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .plans-grid > .reveal {
    border-right: 0;
    border-bottom: 1px solid ${t.border};
  }

  .plans-grid > .reveal:last-child {
    border-bottom: 0;
  }

  .plan-card {
    min-height: 0;
    padding: 22px 0;
  }

  .plan-featured {
    box-shadow: inset 2px 0 0 ${t.accent};
    padding-left: 12px;
  }

  .plan-top p {
    min-height: 0;
  }

  .faq-grid {
    grid-template-columns: 1fr;
    gap: 27px;
  }

  .faq-copy {
    position: static;
  }

  .faq-question {
    min-height: 54px;
  }

  .final-cta {
    padding: 32px 0 90px;
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
  }

  .final-cta .btn {
    width: 100%;
  }

  .footer {
    padding-bottom: 72px;
  }

  .footer-main {
    grid-template-columns: 1fr;
    gap: 26px;
  }

  .footer-links {
    grid-template-columns: 1fr;
  }

  .footer-bottom {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .mobile-sticky {
    position: fixed;
    z-index: 90;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: 58px;
    padding: 7px 14px calc(7px + env(safe-area-inset-bottom));
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-top: 1px solid ${t.borderStrong};
    background: ${t.bg};
    box-shadow: none;
  }

  .mobile-sticky > div {
    min-width: 0;
  }

  .mobile-sticky strong,
  .mobile-sticky span {
    display: block;
  }

  .mobile-sticky strong {
    color: ${t.text};
    font-size: 9px;
    font-weight: 700;
  }

  .mobile-sticky span {
    margin-top: 2px;
    color: ${t.muted};
    font-size: 7.5px;
  }

  .mobile-sticky .btn {
    min-height: 34px;
    padding: 0 12px;
    flex: 0 0 auto;
  }
}

@media (max-width: 390px) {
  .wrap {
    width: calc(100% - 22px);
  }

  .product-topbar {
    padding: 0 10px;
  }

  .notification-card {
    grid-template-columns: 26px minmax(0,1fr);
  }

  .notification-card button {
    grid-column: 1 / -1;
    width: 100%;
  }

  .footer-links {
    gap: 7px;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }

  .reveal {
    opacity: 1 !important;
    transform: none !important;
  }
}
`;
}
